import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Row, Col, Card, CardBody, CardHeader, Spinner, Alert,
} from 'reactstrap';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import Chart from 'react-apexcharts';

import { get } from '../../helpers/api_helper';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import BaseGrid from '../../components/Common/BaseGrid';
import CategoryBreakdownCard from '../../components/PartnerApi/CategoryBreakdownCard';

function PartnerApiDetails(props) {
  const { t } = props;
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await get(`/api/partner/app/${id}/details`);
        setData(response);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  const partner = data?.partner || {};
  const habits = data?.ordering_habits || {};
  const returnRates = data?.return_rates || null;
  const contactName = [partner.contact_firstname, partner.contact_lastname].filter(Boolean).join(' ');
  const formatCurrency = (value) => new Intl.NumberFormat(
    'en-US',
    {
      style: 'currency',
      currency: 'USD',
    },
  ).format(Number(value) || 0);
  const formatReturnRate = (rate) => {
    if (rate == null || rate.return_rate == null) {
      return '--';
    }

    return `${((Number(rate.return_rate) || 0) * 100).toFixed(2)}%`;
  };
  const formatReturnRateDetails = (rate) => {
    if (rate == null) {
      return t('Return rate data is unavailable from the API');
    }

    if (!rate?.earliest_order_at) {
      return t('No Partner API returns in this period');
    }

    return `${Number(rate.returned_order_count) || 0} ${t('returned orders')} / ${Number(rate.order_count) || 0} ${t('orders since')} ${moment(rate.earliest_order_at).format('MMM D, YYYY')}`;
  };
  const profileRows = [
    { label: t('Site'), value: partner.site || '--' },
    { label: t('Tax ID'), value: partner.tax_id || '--' },
    { label: t('Contact'), value: contactName || '--' },
    { label: t('Email'), value: partner.contact_email || '--' },
    { label: t('Telephone'), value: partner.contact_telephone || '--' },
    { label: t('State'), value: partner.state || '--' },
    { label: t('Zip'), value: partner.zip || '--' },
  ];
  const volumePoints = Array.isArray(habits.volume_points) ? habits.volume_points : [];
  const volumeTrendLine = Array.isArray(habits.volume_trend_line) ? habits.volume_trend_line : [];
  const categoryBreakdownParams = {
    platform: 'Partner API',
    site: partner.site || '',
  };
  const volumeMax = volumePoints.reduce(
    (max, point) => ((Number(point.order_count) || 0) > max ? Number(point.order_count) || 0 : max),
    0,
  );

  return (
    <div className="page-content sourcing-supplier-page">
      <Breadcrumbs
        title="Partner API"
        prevLink="/partner-api"
        breadcrumbItem={partner.site || t('Partner Details')}
      />

      <div className="container-fluid">
        {loading && (
          <Card>
            <CardBody className="text-center">
              <Spinner color="primary" />
            </CardBody>
          </Card>
        )}

        {!loading && error !== '' && (
          <Alert color="danger">{error}</Alert>
        )}

        {!loading && error === '' && data !== null && (
          <>
            <Row>
              <Col md="5">
                <Card className="sourcing-supplier-page__panel-card">
                  <CardHeader className="bg-transparent border-bottom sourcing-supplier-page__panel-header">
                    <div className="sourcing-supplier-page__eyebrow">
                      {t('Partner Profile')}
                    </div>
                    <div className="sourcing-supplier-page__panel-title">
                      {partner.site || '--'}
                    </div>
                  </CardHeader>
                  <CardBody className="sourcing-supplier-page__panel-body">
                    <div className="table-responsive sourcing-supplier-page__profile-table-wrap">
                      <table className="table table-bordered table-striped sourcing-supplier-page__profile-table mb-0">
                        <tbody>
                          {profileRows.map((row) => (
                            <tr key={row.label}>
                              <th>{row.label}</th>
                              <td>{row.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>

                <Card className="mt-3 sourcing-supplier-page__panel-card">
                  <CardHeader className="bg-transparent border-bottom sourcing-supplier-page__panel-header">
                    <div className="sourcing-supplier-page__panel-title">
                      {t('Recent Orders')}
                    </div>
                  </CardHeader>
                  <CardBody className="pt-0 sourcing-supplier-page__panel-body">
                    <BaseGrid
                      url="/api/sales"
                      hasSearch
                      filters={`platform=Partner API&site=${encodeURIComponent(partner.site || '')}`}
                      config={{
                        columns: {
                          id: {
                            label: 'ID',
                            sort: true,
                            link: {
                              to: '/sales/%id%',
                            },
                          },
                          reference1: {
                            label: 'Reference',
                            sort: true,
                            link: {
                              to: '/sales/%id%',
                            },
                            format: (value, item) => value || item.reference2 || '--',
                          },
                          total_paid: {
                            label: 'Total',
                            sort: true,
                            format: (value, item) => `${value || 0} ${item.currency_code || ''}`.trim(),
                          },
                          created_at: {
                            label: 'Created At',
                            sort: true,
                            date: {
                              format: (date) => moment(date).fromNow(),
                              timezone: process.env.LOCAL_TZ,
                            },
                          },
                        },
                        pagination: {
                          sort: 'id',
                          direction: 'desc',
                          perPage: 10,
                        },
                      }}
                    />
                  </CardBody>
                </Card>
              </Col>

              <Col md="7">
                <div className="sourcing-supplier-page__metrics-head mb-3">
                  <div className="sourcing-supplier-page__eyebrow">
                    {t('Performance')}
                  </div>
                  <div className="sourcing-supplier-page__metrics-title">
                    {t('Revenue, Returns & Ordering Habits')}
                  </div>
                </div>

                <Row className="g-3 mb-3">
                  <Col md="4">
                    <Card className="sourcing-supplier-page__stat-card h-100">
                      <CardBody>
                        <div className="sourcing-supplier-page__stat-label">{t('30-Day Revenue')}</div>
                        <div className="sourcing-supplier-page__stat-value">{formatCurrency(habits.last_30_days_revenue)}</div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="4">
                    <Card className="sourcing-supplier-page__stat-card h-100">
                      <CardBody>
                        <div className="sourcing-supplier-page__stat-label">{t('1 Year Revenue')}</div>
                        <div className="sourcing-supplier-page__stat-value">{formatCurrency(habits.last_year_revenue)}</div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="4">
                    <Card className="sourcing-supplier-page__stat-card h-100">
                      <CardBody>
                        <div className="sourcing-supplier-page__stat-label">{t('All Time Revenue')}</div>
                        <div className="sourcing-supplier-page__stat-value">{formatCurrency(habits.all_time_revenue)}</div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row className="g-3 mb-3">
                  <Col md="6">
                    <Card className="sourcing-supplier-page__stat-card h-100">
                      <CardBody>
                        <div className="sourcing-supplier-page__stat-label">{t('90-Day Return Rate')}</div>
                        <div className="sourcing-supplier-page__stat-value">
                          {formatReturnRate(returnRates?.last_90_days)}
                        </div>
                        <div className="sourcing-supplier-page__metric-footnote">
                          {formatReturnRateDetails(returnRates?.last_90_days)}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="6">
                    <Card className="sourcing-supplier-page__stat-card h-100">
                      <CardBody>
                        <div className="sourcing-supplier-page__stat-label">{t('All-Time Return Rate')}</div>
                        <div className="sourcing-supplier-page__stat-value">
                          {formatReturnRate(returnRates?.all_time)}
                        </div>
                        <div className="sourcing-supplier-page__metric-footnote">
                          {formatReturnRateDetails(returnRates?.all_time)}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Card className="sourcing-supplier-page__stat-card">
                  <CardHeader className="bg-transparent border-bottom sourcing-supplier-page__panel-header">
                    <div className="sourcing-supplier-page__panel-title">{t('Ordering Habits')}</div>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md="4">
                        <div className="sourcing-supplier-page__stat-label">{t('Total Orders')}</div>
                        <div className="sourcing-supplier-page__stat-value sourcing-supplier-page__stat-value--compact">{habits.total_orders || 0}</div>
                      </Col>
                      <Col md="4">
                        <div className="sourcing-supplier-page__stat-label">{t('Orders Last 30 Days')}</div>
                        <div className="sourcing-supplier-page__stat-value sourcing-supplier-page__stat-value--compact">{habits.orders_last_30_days || 0}</div>
                      </Col>
                      <Col md="4">
                        <div className="sourcing-supplier-page__stat-label">{t('Average Order Total')}</div>
                        <div className="sourcing-supplier-page__stat-value sourcing-supplier-page__stat-value--compact">{formatCurrency(habits.average_order_total)}</div>
                      </Col>
                    </Row>

                    <Row className="mt-3">
                      <Col md="4">
                        <div className="sourcing-supplier-page__stat-label">{t('Gross Order Value')}</div>
                        <div className="sourcing-supplier-page__stat-value sourcing-supplier-page__stat-value--compact">{formatCurrency(habits.total_revenue)}</div>
                      </Col>
                      <Col md="4">
                        <div className="sourcing-supplier-page__stat-label">{t('First Order')}</div>
                        <div className="sourcing-supplier-page__metric-footnote">
                          {habits.first_order_at ? moment(habits.first_order_at).format('YYYY-MM-DD h:mm A') : '--'}
                        </div>
                      </Col>
                      <Col md="4">
                        <div className="sourcing-supplier-page__stat-label">{t('Last Order')}</div>
                        <div className="sourcing-supplier-page__metric-footnote">
                          {habits.last_order_at ? moment(habits.last_order_at).format('YYYY-MM-DD h:mm A') : '--'}
                        </div>
                      </Col>
                    </Row>

                    <div className="mt-4">
                      <div className="text-muted small text-uppercase mb-3">
                        {t('All-Time Order Volume')}
                      </div>
                      {volumePoints.length > 0 ? (
                        <Chart
                          type="line"
                          height={280}
                          series={[
                            {
                              name: t('Orders'),
                              data: volumePoints.map((point) => ({
                                x: point.date,
                                y: Number(point.order_count) || 0,
                              })),
                            },
                            {
                              name: t('Trend'),
                              data: volumePoints.map((point, index) => ({
                                x: point.date,
                                y: Number(volumeTrendLine[index]) || 0,
                              })),
                            },
                          ]}
                          options={{
                            chart: {
                              toolbar: { show: false },
                              zoom: { enabled: false },
                            },
                            colors: ['#3b82f6', '#94a3b8'],
                            stroke: {
                              width: [3, 2],
                              curve: 'smooth',
                              dashArray: [0, 6],
                            },
                            markers: {
                              size: [3, 0],
                            },
                            dataLabels: {
                              enabled: false,
                            },
                            xaxis: {
                              type: 'datetime',
                              labels: {
                                datetimeUTC: false,
                                format: 'MMM yyyy',
                              },
                            },
                            yaxis: {
                              title: {
                                text: t('Orders'),
                              },
                              min: 0,
                              max: volumeMax > 0 ? undefined : 1,
                              forceNiceScale: true,
                              labels: {
                                formatter: (value) => Number(value).toFixed(0),
                              },
                            },
                            tooltip: {
                              shared: true,
                              x: {
                                format: 'MMM yyyy',
                              },
                              y: {
                                formatter: (value, opts) => {
                                  if (opts.seriesIndex === 0) {
                                    return `${Number(value).toFixed(0)} ${t('orders')}`;
                                  }

                                  return `${Number(value).toFixed(2)} ${t('trend')}`;
                                },
                              },
                            },
                            legend: {
                              position: 'top',
                            },
                            grid: {
                              borderColor: 'rgba(148, 163, 184, 0.18)',
                            },
                            noData: {
                              text: t('No order volume data'),
                            },
                          }}
                        />
                      ) : (
                        <div className="text-muted">
                          {t('No order volume data')}
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>

                <CategoryBreakdownCard
                  title={t('Category Trees')}
                  params={categoryBreakdownParams}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md="12">
                <Card className="sourcing-supplier-page__panel-card">
                  <CardHeader className="bg-transparent border-bottom sourcing-supplier-page__panel-header">
                    <div className="sourcing-supplier-page__panel-title">
                      {t('Returns')}
                    </div>
                  </CardHeader>
                  <CardBody className="pt-0 sourcing-supplier-page__panel-body">
                    <BaseGrid
                      url="/api/partner/returns/top"
                      hasSearch
                      filters={`platform=Partner API&site=${encodeURIComponent(partner.site || '')}`}
                      config={{
                        columns: {
                          sku: {
                            label: 'SKU',
                            sort: true,
                          },
                          title: {
                            label: 'Title',
                            sort: true,
                          },
                          returned_qty: {
                            label: 'Returned Qty',
                            sort: true,
                          },
                          return_count: {
                            label: 'Return Count',
                            sort: true,
                          },
                          latest_return_at: {
                            label: 'Latest Return',
                            sort: true,
                            date: {
                              format: (date) => moment(date).fromNow(),
                              timezone: process.env.LOCAL_TZ,
                            },
                          },
                          return_class_counts: {
                            label: 'Return Classes',
                            sort: false,
                            translate: false,
                            format: (value) => {
                              if (!Array.isArray(value) || value.length === 0) {
                                return '--';
                              }

                              return value.map((entry) => (
                                <div
                                  key={`${entry.return_class}-${entry.class_count}`}
                                  className="mb-1"
                                >
                                  {`${entry.return_class} (${entry.class_count})`}
                                </div>
                              ));
                            },
                          },
                        },
                        pagination: {
                          sort: 'returned_qty',
                          direction: 'desc',
                          perPage: 10,
                        },
                      }}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
}

PartnerApiDetails.propTypes = {
  t: PropTypes.func.isRequired,
};

export default connect(null, {})(withTranslation()(PartnerApiDetails));
